import React, { useState, useEffect, useCallback } from 'react';
import { heroSlides } from '../../data/dreamlandData';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section className="relative w-full h-[85vh] overflow-hidden">
      {/* Slides */}
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className="absolute inset-0 transition-all duration-1000 ease-in-out"
          style={{
            opacity: currentSlide === index ? 1 : 0,
            transform: currentSlide === index ? 'scale(1)' : 'scale(1.08)',
            zIndex: currentSlide === index ? 1 : 0,
          }}
        >
          {/* Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${slide.image})` }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-white text-center px-4">
        <div
          key={currentSlide}
          style={{ animation: 'fadeInUp 0.8s ease-out' }}
        >
          <p className="text-lg md:text-xl tracking-[0.3em] uppercase text-[#E4C18B] mb-4 font-light">
            Welcome to
          </p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold font-['Playfair_Display'] mb-6 leading-tight">
            Dreamland Inn
          </h1>
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="w-16 h-[1px] bg-[#E4C18B]" />
            <span className="w-2 h-2 bg-[#E4C18B] rotate-45" />
            <span className="w-16 h-[1px] bg-[#E4C18B]" />
          </div>
          <p className="text-xl md:text-2xl font-['Dancing_Script'] text-white/90">
            {heroSlides[currentSlide].subtitle}
          </p>
          <a
            href="/#rooms"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('rooms')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-block mt-10 px-8 py-3.5 border-2 border-[#E4C18B] text-[#E4C18B] text-sm font-semibold tracking-wider uppercase hover:bg-[#C8964E] hover:border-[#C8964E] hover:text-white transition-all duration-300"
          >
            Explore Rooms
          </a>
        </div>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#C8964E] transition-all duration-300 border border-white/20"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#C8964E] transition-all duration-300 border border-white/20"
      >
        <ChevronRight size={22} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              currentSlide === index
                ? 'w-8 h-2.5 bg-[#C8964E]'
                : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#FDFBF7] to-transparent z-10" />
    </section>
  );
};

export default HeroSlider;
